import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { AppId } from "../../../../app/appTypes";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { DeleteButton } from "../../../../app/components/DeleteButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { deleteDqMapping } from "../documentDqMappingsApi";

export interface DeleteDqMappingDialogProps {
    documentId: AppId;
    sectionId: string;
    mappingId: number;
    onSuccessfulSave: (mappingId: number) => void;
    onCancel: () => void;
}

export const DeleteDqMappingDialog = ({
    documentId,
    sectionId,
    mappingId,
    onSuccessfulSave,
    onCancel,
}: DeleteDqMappingDialogProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const handleConfirmDeleteClick = async () => {
        setSaving(true);
        try {
            const result = await dispatch(deleteDqMapping(
                { documentId, sectionId, mappingId }));
            if (deleteDqMapping.fulfilled.match(result)) {
                dispatch(showSuccess("deleted"));
                onSuccessfulSave(mappingId);
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="xs">
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                <Localized id="confirm-delete">
                    <span>_delete warning</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmberIcon color="error" />
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="document-dq-mapping-delete-delete-warning">
                            <span>_delete warning</span>
                        </Localized>
                    </Typography>
                </Box>
                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <DeleteButton loading={saving} onClick={handleConfirmDeleteClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
